// Codey Config Viewer - Frontend

interface ConfigFile {
  name: string;
  path: string;
  content: string;
  exists: boolean;
}

// State
let files: ConfigFile[] = [];
let selectedFile: ConfigFile | null = null;
let originalContent = "";
let hasChanges = false;

// DOM Elements
const loginView = document.getElementById("login-view") as HTMLDivElement;
const configView = document.getElementById("config-view") as HTMLDivElement;
const loginForm = document.getElementById("login-form") as HTMLFormElement;
const passwordInput = document.getElementById("password-input") as HTMLInputElement;
const loginError = document.getElementById("login-error") as HTMLParagraphElement;
const logoutBtn = document.getElementById("logout-btn") as HTMLButtonElement;
const fileToggle = document.getElementById("file-toggle") as HTMLButtonElement;
const sidebarOverlay = document.getElementById("sidebar-overlay") as HTMLDivElement;
const fileList = document.getElementById("file-list") as HTMLDivElement;
const noFileSelected = document.getElementById("no-file-selected") as HTMLDivElement;
const fileEditor = document.getElementById("file-editor") as HTMLDivElement;
const fileName = document.getElementById("file-name") as HTMLHeadingElement;
const filePath = document.getElementById("file-path") as HTMLSpanElement;
const fileTextarea = document.getElementById("file-textarea") as HTMLTextAreaElement;
const saveBtn = document.getElementById("save-btn") as HTMLButtonElement;
const saveStatus = document.getElementById("save-status") as HTMLParagraphElement;

// Check authentication status
async function checkAuth(): Promise<boolean> {
  try {
    const response = await fetch("/api/auth/status");
    const data = (await response.json()) as { authenticated?: boolean };
    return data.authenticated === true;
  } catch {
    return false;
  }
}

// Show the appropriate view
function showView(authenticated: boolean): void {
  if (authenticated) {
    loginView.classList.add("hidden");
    configView.classList.remove("hidden");
    loadFiles();
  } else {
    loginView.classList.remove("hidden");
    configView.classList.add("hidden");
    passwordInput.focus();
  }
}

// Handle login
async function handleLogin(event: Event): Promise<void> {
  event.preventDefault();
  loginError.textContent = "";

  const password = passwordInput.value;
  if (!password) return;

  try {
    const response = await fetch("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });

    const data = (await response.json()) as { error?: string };

    if (response.ok) {
      passwordInput.value = "";
      showView(true);
    } else {
      loginError.textContent = data.error || "Login failed";
      passwordInput.select();
    }
  } catch {
    loginError.textContent = "Connection error. Please try again.";
  }
}

// Handle logout
async function handleLogout(): Promise<void> {
  try {
    await fetch("/api/logout", { method: "POST" });
    files = [];
    selectedFile = null;
    showView(false);
  } catch (error) {
    console.error("Logout error:", error);
  }
}

function openSidebar(): void {
  configView.classList.add("sidebar-open");
  sidebarOverlay.classList.remove("hidden");
  sidebarOverlay.setAttribute("aria-hidden", "false");
}

function closeSidebar(): void {
  configView.classList.remove("sidebar-open");
  sidebarOverlay.classList.add("hidden");
  sidebarOverlay.setAttribute("aria-hidden", "true");
}

function toggleSidebar(): void {
  if (configView.classList.contains("sidebar-open")) {
    closeSidebar();
  } else {
    openSidebar();
  }
}

// Load config files
async function loadFiles(): Promise<void> {
  fileList.innerHTML = '<p class="loading">Loading files...</p>';

  try {
    const response = await fetch("/api/config/files");

    if (!response.ok) {
      if (response.status === 401) {
        showView(false);
        return;
      }
      throw new Error("Failed to load files");
    }

    const data = (await response.json()) as { files: ConfigFile[] };
    files = data.files;
    renderFileList();
  } catch (error) {
    console.error("Error loading files:", error);
    fileList.innerHTML = '<p class="loading">Failed to load files</p>';
  }
}

// Render file list
function renderFileList(): void {
  if (files.length === 0) {
    fileList.innerHTML = '<p class="loading">No config files found</p>';
    return;
  }

  fileList.innerHTML = files
    .map(
      (file) => `
      <div class="file-item${selectedFile?.path === file.path ? " active" : ""}" data-path="${file.path}">
        <div class="file-item-name">${escapeHtml(file.name)}</div>
        <div class="file-item-path">${escapeHtml(file.path)}</div>
      </div>
    `
    )
    .join("");

  // Add click handlers
  fileList.querySelectorAll(".file-item").forEach((item) => {
    item.addEventListener("click", () => {
      const path = item.getAttribute("data-path");
      if (path) selectFile(path);
    });
  });
}

// Select a file
async function selectFile(path: string): Promise<void> {
  // Check for unsaved changes
  if (hasChanges) {
    const confirm = window.confirm("You have unsaved changes. Discard them?");
    if (!confirm) return;
  }

  try {
    const response = await fetch(`/api/config/file?path=${encodeURIComponent(path)}`);

    if (!response.ok) {
      throw new Error("Failed to load file");
    }

    const data = (await response.json()) as { file: ConfigFile };
    selectedFile = data.file;
    originalContent = data.file.content;
    hasChanges = false;

    showEditor();
    renderFileList();

    if (window.matchMedia("(max-width: 768px)").matches) {
      closeSidebar();
    }
  } catch (error) {
    console.error("Error loading file:", error);
    saveStatus.textContent = "Failed to load file";
    saveStatus.className = "save-status error";
  }
}

// Show the editor
function showEditor(): void {
  if (!selectedFile) return;

  noFileSelected.classList.add("hidden");
  fileEditor.classList.remove("hidden");

  fileName.textContent = selectedFile.name;
  filePath.textContent = selectedFile.path;
  fileTextarea.value = selectedFile.content;
  saveBtn.disabled = true;
  saveStatus.textContent = "";
}

// Handle textarea changes
function handleTextareaChange(): void {
  if (!selectedFile) return;

  hasChanges = fileTextarea.value !== originalContent;
  saveBtn.disabled = !hasChanges;
  saveStatus.textContent = hasChanges ? "Unsaved changes" : "";
  saveStatus.className = "save-status";
}

// Save file
async function handleSave(): Promise<void> {
  if (!selectedFile || !hasChanges) return;

  saveBtn.disabled = true;
  saveStatus.textContent = "Saving...";
  saveStatus.className = "save-status";

  try {
    const response = await fetch("/api/config/file", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: selectedFile.path,
        content: fileTextarea.value,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to save file");
    }

    originalContent = fileTextarea.value;
    selectedFile.content = fileTextarea.value;
    hasChanges = false;
    saveBtn.disabled = true;
    saveStatus.textContent = "Saved successfully";
    saveStatus.className = "save-status success";

    // Clear success message after 3 seconds
    setTimeout(() => {
      if (saveStatus.textContent === "Saved successfully") {
        saveStatus.textContent = "";
      }
    }, 3000);
  } catch (error) {
    console.error("Error saving file:", error);
    saveBtn.disabled = false;
    saveStatus.textContent = "Failed to save file";
    saveStatus.className = "save-status error";
  }
}

// Escape HTML
function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

// Handle keyboard shortcuts
function handleKeydown(event: KeyboardEvent): void {
  // Cmd/Ctrl + S to save
  if ((event.metaKey || event.ctrlKey) && event.key === "s") {
    event.preventDefault();
    if (!saveBtn.disabled) {
      handleSave();
    }
  }
}

// Initialize
async function init(): Promise<void> {
  // Set up event listeners
  loginForm.addEventListener("submit", handleLogin);
  logoutBtn.addEventListener("click", handleLogout);
  fileToggle.addEventListener("click", toggleSidebar);
  sidebarOverlay.addEventListener("click", closeSidebar);
  fileTextarea.addEventListener("input", handleTextareaChange);
  saveBtn.addEventListener("click", handleSave);
  document.addEventListener("keydown", handleKeydown);

  // Warn about unsaved changes on page leave
  window.addEventListener("beforeunload", (e) => {
    if (hasChanges) {
      e.preventDefault();
      e.returnValue = "";
    }
  });

  // Check auth and show appropriate view
  const authenticated = await checkAuth();
  showView(authenticated);
}

init();
